import {
  ISearchPageFilter,
  ISearchPageFilterValue,
  TypePrice,
} from 'api/types/searchPageApiTypes';
import { ListSingleSelect } from 'components/DealerPage/components/Filters/ListSingleSelect/ListSingleSelect';
import { PriceSelectRange } from 'components/DealerPage/components/Filters/PriceSelectRange/PriceSelectRange';
import { PriceInputRange } from 'components/DealerPage/components/Filters/PriceInputRange/PriceInputRange';
import { TextInputRange } from 'components/DealerPage/components/Filters/TextInputRange/TextInputRange';
import {
  ListMultiSelect,
  ListMultiSelectOption,
} from 'components/DealerPage/components/Filters/ListMultiSelect/ListMultiSelect';
import debounce from 'lodash/debounce';
import { CURRENCIES } from 'types';
import { ListMultiSelectTag } from 'components/DealerPage/components/Filters/ListMultiSelectTag/ListMultiSelectTag';
import { ListSingleSelectTag } from 'components/DealerPage/components/Filters/ListSingleSelectTag/ListSingleSelectTag';
import { DropDownRange } from 'components/DealerPage/components/Filters/DropDownRange/DropDownRange';
import { PriceSelectMonthRange } from 'components/DealerPage/components/Filters/PriceSelectMonthRange/PriceSelectMonthRange';
import { Checkbox } from 'components/Toolkit/Inputs/Checkbox';

import {
  useDealerFilterState,
  useDealerFilterDispatch,
} from 'components/DealerPage/context/DealerFilterContext';
import {
  mapToMultiSelect,
  mapToPriceSelect,
  mapToSingleSelect,
} from 'components/DealerPage/mappers/filters';
import type { RangeType } from 'components/DealerPage/hooks/useFilters';
import { ListMultiSelectMakeModel } from 'components/DealerPage/components/Filters/ListMultiSelectMakeModel/ListMultiSelectMakeModel';

export interface IFiltersProps {
  section: string;
  filtersData: ISearchPageFilter[];
  dealerId?: string;
}

const mapCurrency = (currency?: CURRENCIES) =>
  currency === CURRENCIES.GBP ? '£' : '€';

function Filters({ filtersData, section, dealerId }: IFiltersProps) {
  const {
    ranges: rangeValues,
    filters: filterValues,
    makeModelData,
    makeModel,
    isAddAnotherEnabled,
  } = useDealerFilterState();

  const {
    setFilter: onChangeFilter,
    setRange: onChangeRange,
    setFilterAndRange: onChangeFilterAndRange,
    addMultiSelectValue,
    removeMultiSelectValue,
    getFilterMultiValue,
    getFilterValue,
    addNewMakeModel,
    handleOnUpdateMake,
    handleOnAddModel,
    handleOnRemoveModel,
    handleResetModel,
    handleOnRemoveMakeModel,
    reset,
  } = useDealerFilterDispatch();

  const getTextInputDisplayName = (filters: ISearchPageFilterValue[]) => {
    const filtersLength = filters.length;
    const firstFiltersValue = filters[0].value;
    const firstFiltersDisplayName = filters[0].displayName;
    if (filtersLength && firstFiltersValue === '')
      return filters[0].displayName;
    else if (filtersLength > 1)
      return `${firstFiltersDisplayName} (+${filtersLength - 1})`;
    else return firstFiltersDisplayName;
  };

  const containsValidPriceFilter: boolean = filtersData.some(
    (filter) => filter.name === 'containsValidPrice',
  );

  function handleMultiFilterChange(
    option: ListMultiSelectOption,
    isChecked: boolean,
    name: string,
  ) {
    if (isChecked) {
      removeMultiSelectValue(name, option.value);
    } else {
      !!option.value.trim()
        ? addMultiSelectValue({
            name: name,
            values: [option.value],
          })
        : handleMultiFilterClear(name);
    }
  }

  function handleMultiFilterClear(name: string) {
    onChangeFilter({ name: name, values: [] });
  }

  function getRangeValue(filter: ISearchPageFilter) {
    const range = rangeValues?.find(
      (range: RangeType) => range.name === filter.name,
    );
    return range
      ? {
          from: range.from,
          to: range.to,
          currency: range.currency,
          typePrice: range.typePrice,
        }
      : undefined;
  }

  function handleFilterChange(name: string, value: string) {
    onChangeFilter({ name: name, values: [value] });
  }

  function handleRangeValidationChange(
    name: string,
    from?: string,
    to?: string,
    currency?: CURRENCIES,
    typePrice?: TypePrice,
  ) {
    if (Number(from) > Number(to)) {
      onChangeRange({ name, from, to: '', currency, typePrice });
    } else {
      onChangeRange({ name, from, to, currency, typePrice });
    }
  }

  function handlePriceChange(
    name: string,
    from?: string,
    to?: string,
    currency?: CURRENCIES,
    typePrice?: TypePrice,
  ) {
    if (containsValidPriceFilter) {
      const showValidPrice = Boolean(from || to) ? 'true' : '';

      onChangeFilterAndRange(
        { name: 'containsValidPrice', values: [showValidPrice] },
        { name, from, to, currency, typePrice },
      );
    } else {
      onChangeRange({ name, from, to, currency, typePrice });
    }
  }

  function handleSingleSelect(
    name: string,
    filterValue?: ISearchPageFilterValue,
  ) {
    if (filterValue?.type === 'MIN') {
      onChangeRange({ name, from: filterValue.value });
    }
    if (filterValue?.type === 'MAX') {
      onChangeRange({ name, to: filterValue.value });
    }
  }

  function handleInputRange(name: string, from?: string, to?: string) {
    onChangeRange({ name, from, to });
  }

  function getSingleSelect(filter: ISearchPageFilter) {
    const range = rangeValues?.find(
      (range: RangeType) => range.name === filter.name,
    );
    if (range?.from) return range.from;
    else if (range?.to) return range.to;
    else if (filter.values && filter.values[0].value)
      return filter.values[0].value;
    else return '';
  }

  function handleCheckbox(name: string, checked: boolean) {
    if (name === 'containsValidPrice' && !checked) {
      onChangeFilter({
        name: 'containsValidPrice',
        values: [],
      });
    } else {
      handleFilterChange(name, checked.toString());
    }
  }

  function renderFilter(filter: ISearchPageFilter) {
    switch (filter.filterType.name) {
      default:
        switch (filter.filterType.name) {
          case 'ListSingleSelect':
            switch (filter.variant) {
              case 'DONEDEAL_TAG': {
                return (
                  <ListSingleSelectTag
                    name={filter.name}
                    label={filter.displayName}
                    options={mapToSingleSelect(
                      filter.values as ISearchPageFilterValue[],
                    )}
                    onChange={(newValue: string) =>
                      handleFilterChange(filter.name, newValue)
                    }
                    value={getFilterValue(filter)}
                  />
                );
              }
              default: {
                return (
                  <ListSingleSelect
                    name={filter.name}
                    label={filter.displayName}
                    options={mapToSingleSelect(
                      filter.values as ISearchPageFilterValue[],
                    )}
                    onChange={(newValue: string) =>
                      handleFilterChange(filter.name, newValue)
                    }
                    value={getFilterValue(filter)}
                  />
                );
              }
            }
          case 'ListMultiSelect':
            switch (filter.variant) {
              case 'DONEDEAL_TAG': {
                return (
                  <ListMultiSelectTag
                    name={filter.name}
                    label={filter.displayName}
                    placeholder={
                      getTextInputDisplayName(getFilterMultiValue(filter)) ?? ''
                    }
                    options={mapToMultiSelect(
                      filter.values as ISearchPageFilterValue[],
                    )}
                    selectedOptions={mapToMultiSelect(
                      getFilterMultiValue(filter),
                    )}
                    onSelectOption={(option, isChecked) => {
                      return handleMultiFilterChange(
                        option,
                        isChecked,
                        filter.name,
                      );
                    }}
                    onClear={() => handleMultiFilterClear(filter.name)}
                  />
                );
              }
              case 'DONEDEAL_MAKE_MODEL': {
                return (
                  <ListMultiSelectMakeModel
                    section={section}
                    makeModel={makeModel}
                    makeModelData={makeModelData}
                    addNewMakeModel={addNewMakeModel}
                    handleOnUpdateMake={handleOnUpdateMake}
                    handleOnAddModel={handleOnAddModel}
                    handleOnRemoveMakeModel={handleOnRemoveMakeModel}
                    handleOnRemoveModel={handleOnRemoveModel}
                    handleResetModel={handleResetModel}
                    reset={reset}
                    isAddAnotherEnabled={isAddAnotherEnabled}
                    dealerId={dealerId}
                  />
                );
              }

              default: {
                return (
                  <ListMultiSelect
                    name={filter.name}
                    label={filter.displayName}
                    placeholder={
                      getTextInputDisplayName(getFilterMultiValue(filter)) ?? ''
                    }
                    options={mapToMultiSelect(
                      filter.values as ISearchPageFilterValue[],
                    )}
                    selectedOptions={mapToMultiSelect(
                      getFilterMultiValue(filter),
                    )}
                    onSelectOption={(option, isChecked) =>
                      handleMultiFilterChange(option, isChecked, filter.name)
                    }
                    onClear={() => handleMultiFilterClear(filter.name)}
                  />
                );
              }
            }
          case 'TextInputRange':
            return (
              <TextInputRange
                label={filter.displayName}
                value={getRangeValue(filter)}
                onChange={debounce(
                  (from, to) => handleInputRange(filter.name, from, to),
                  500,
                )}
              />
            );
          case 'PriceInputRange':
            switch (filter.variant) {
              case 'DONEDEAL_PRICE_DROPDOWN': {
                const priceRangeValues =
                  filter.values &&
                  (filter.values[0] as unknown as {
                    from: ISearchPageFilterValue[];
                    to: ISearchPageFilterValue[];
                  });
                const rangePriceValues = getRangeValue(filter);
                const currency = mapCurrency(rangePriceValues?.currency);
                return (
                  <PriceSelectRange
                    from={{
                      options: mapToPriceSelect(
                        priceRangeValues?.from,
                        currency,
                      ),
                      selectedValue: rangePriceValues?.from || '',
                    }}
                    to={{
                      options: mapToPriceSelect(priceRangeValues?.to, currency),
                      selectedValue: rangePriceValues?.to || '',
                    }}
                    value={getRangeValue(filter)}
                    onChangePrice={debounce(
                      (currency, from, to) =>
                        handleRangeValidationChange(
                          filter.name,
                          from,
                          to,
                          currency,
                        ),
                      500,
                    )}
                  />
                );
              }
              case 'DONEDEAL_PRICE_DROPDOWN_PPM': {
                const priceRangePPMDefaultValues =
                  filter.values &&
                  (filter.values[0] as unknown as {
                    from: ISearchPageFilterValue[];
                    pricePerMonthFrom: ISearchPageFilterValue[];
                    to: ISearchPageFilterValue[];
                    pricePerMonthTo: ISearchPageFilterValue[];
                  });
                const rangePriceValues = getRangeValue(filter);
                const currency = mapCurrency(rangePriceValues?.currency);
                const carFinanceFilter = filterValues?.find(
                  (item) => item.name === 'carFinance',
                );
                const typePrice: TypePrice =
                  (carFinanceFilter && carFinanceFilter.values.length
                    ? carFinanceFilter.values[0] === 'true'
                      ? 'pricePerMonth'
                      : 'price'
                    : rangePriceValues?.typePrice) || 'price';

                return (
                  <PriceSelectMonthRange
                    typePrice={typePrice}
                    from={{
                      options: mapToPriceSelect(
                        priceRangePPMDefaultValues?.from,
                        currency,
                      ),
                      selectedValue: rangePriceValues?.from || '',
                    }}
                    to={{
                      options: mapToPriceSelect(
                        priceRangePPMDefaultValues?.to,
                        currency,
                      ),
                      selectedValue: rangePriceValues?.to || '',
                    }}
                    fromMonth={{
                      options: mapToPriceSelect(
                        priceRangePPMDefaultValues?.pricePerMonthFrom,
                        currency,
                      ),
                      selectedValue: rangePriceValues?.from || '',
                    }}
                    toMonth={{
                      options: mapToPriceSelect(
                        priceRangePPMDefaultValues?.pricePerMonthTo,
                        currency,
                      ),
                      selectedValue: rangePriceValues?.to || '',
                    }}
                    onChangePrice={(currency, from, to, typePrice) =>
                      handleRangeValidationChange(
                        filter.name,
                        from,
                        to,
                        currency,
                        typePrice,
                      )
                    }
                    onChangeType={(currency, type) => {
                      let newCurrency = currency;
                      if (type === 'pricePerMonth') {
                        onChangeFilter({
                          name: 'carFinance',
                          values: ['true'],
                        });
                        // set currency back to eur for PPM
                        newCurrency = CURRENCIES.EUR;
                      } else {
                        onChangeFilter({ name: 'carFinance', values: [] });
                      }
                      // Reset from and to
                      handleRangeValidationChange(
                        filter.name,
                        undefined,
                        undefined,
                        newCurrency,
                        type,
                      );
                    }}
                    value={rangePriceValues}
                  />
                );
              }
              default:
                return (
                  <PriceInputRange
                    value={getRangeValue(filter)}
                    onChangePrice={debounce(
                      (currency, from, to) =>
                        handlePriceChange(filter.name, from, to, currency),
                      500,
                    )}
                  />
                );
            }
          case 'DropDownRange':
            const [values] = filter.values as ISearchPageFilterValue[];
            const { from: fromValues, to: toValues } = values;
            const dropDownRangeFilter = {
              from: fromValues,
              to: toValues,
            };
            const rangeValues = getRangeValue(filter);
            const { from, to } = dropDownRangeFilter;
            return (
              <>
                <DropDownRange
                  label={filter.displayName}
                  name={filter.name}
                  from={{
                    options: mapToSingleSelect(from),
                    selectedValue: rangeValues?.from || '',
                  }}
                  to={{
                    options: mapToSingleSelect(to),
                    selectedValue: rangeValues?.to || '',
                  }}
                  onFromChange={(value: string) =>
                    handleRangeValidationChange(
                      filter.name,
                      value,
                      rangeValues?.to,
                    )
                  }
                  onToChange={(value: string) =>
                    handleRangeValidationChange(
                      filter.name,
                      rangeValues?.from,
                      value,
                    )
                  }
                />
              </>
            );
          case 'SingleSelectRange':
            return (
              <ListSingleSelect
                name={filter.name}
                label={filter.displayName}
                options={mapToSingleSelect(
                  filter.values as ISearchPageFilterValue[],
                )}
                onChange={(newValue: string) => {
                  const filterSelected = filter.values?.find(
                    (f) => f.value === newValue,
                  );
                  return handleSingleSelect(filter.name, filterSelected);
                }}
                value={getSingleSelect(filter)}
              />
            );
          case 'CheckBox': {
            return (
              <Checkbox
                label={filter.displayName}
                name={filter.name}
                onChange={(checked: boolean) =>
                  handleCheckbox(filter.name, checked)
                }
                checked={Boolean(getFilterValue(filter))}
              />
            );
          }
          default:
            return null;
        }
    }
  }
  return (
    <div>
      {filtersData && (
        <ul>
          {filtersData?.map((item: ISearchPageFilter, index: number) => (
            <li key={index}>{renderFilter(item)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export { Filters };
